/**
 * Reduces a query string to the parameters that may reach analytics.
 *
 * Query parameters are the one payload here that is not built from an allowlist, because the
 * point of them is ad-hoc cohort analysis on parameters nobody has thought of yet. So what may
 * travel is bounded from the other direction: the fields we know are dangerous are dropped by
 * name, and a shape check covers the ones we have not met.
 *
 * Applied to the pre-consent events and the identified ones alike. Being stricter before
 * consent than after would be backwards — `prefill[dateOfBirth]` has no place in a behavioural
 * analytics stream whatever the visitor agreed to.
 *
 * Runs client-side, before the request is built, so a dropped value never leaves the browser.
 *
 * Kept in sync with src/shared/helpers/sanitiseParams.js in amplify-website-v2-frontend.
 */

// Dropped by name, because we know what they carry.
const DROPPED_PARAM_NAMES = new Set([
    // Click identifiers. Each resolves to an ad platform's own profile of the person, so they
    // are identifiers rather than campaign metadata.
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'msclkid',
    'ttclid',
    'twclid',
    'li_fat_id',
    'epik',
    // A partner's own identifier for the person. Legitimate on the lead record, which is
    // operational, but not in analytics.
    'partner_user_id',
]);

// pages/_app.tsx reads these by name: date of birth, gender, zip, household income, total debt
// and retirement savings all arrive this way on a prefill link.
const PREFILL_PREFIX = 'prefill[';

// For the parameters a name list cannot anticipate — someone adding ?email= to a marketing URL
// next year. A 10-digit run is treated as a phone number, which also drops long numeric ids;
// that is deliberate, since a value we cannot identify is not worth the risk of forwarding.
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_LIKE = /^\+?[\d\s().-]{10,}$/;

const MAX_VALUE_LENGTH = 100;
const MAX_PARAMS = 25;

export const sanitiseParams = (params: Record<string, string>): Record<string, string> => {
    const safe: Record<string, string> = {};

    Object.entries(params || {}).forEach(([key, value]) => {
        if (Object.keys(safe).length >= MAX_PARAMS) return;
        if (key.startsWith(PREFILL_PREFIX)) return;
        if (DROPPED_PARAM_NAMES.has(key.toLowerCase())) return;
        if (typeof value !== 'string' || value.length > MAX_VALUE_LENGTH) return;
        if (EMAIL_LIKE.test(value) || PHONE_LIKE.test(value)) return;

        safe[key] = value;
    });

    return safe;
};

/** Convenience for the common case: the current URL's query string, sanitised. */
export const currentSanitisedParams = (): Record<string, string> =>
    sanitiseParams(Object.fromEntries(new URLSearchParams(window.location.search)));

export default sanitiseParams;
